/**
 * Auth Server Functions
 * Secure authentication operations that run on the server
 */

import { createServerFn } from '@tanstack/react-start';
import { API_CONFIG, API_ENDPOINTS, getCookieName } from '@/lib/config/api.config';
import { loadSessionWithRefresh, type SessionLoadResult } from '@/lib/server-fns/sessionLoader';
import { logoutWithBackend } from '@/lib/server-fns/logoutLoader';
import { clientIpForwardHeaders } from '@/lib/server-fns/ssr-fetch';
import { extractCookieHeaderValue } from '@/lib/server-utils/authCookies';
import { setRequestCookieOverlay } from '@/lib/server-utils/requestCookieOverlay';
import { logger } from '@/lib/utils/logger';

/**
 * Telegram Auth Verify Request (OAuth Flow)
 * Used for verifying challenge code from Telegram bot
 */
export interface TelegramAuthVerifyRequest {
  auth_code: string;
  challenge_code: string;
}

/**
 * User Session
 * Represents the authenticated user's data from the backend
 * Note: favorite_count is computed client-side from favorites data
 */
export interface UserSession {
  id: string;
  telegram_id: number;
  telegram_username?: string;
  telegram_first_name: string;
  telegram_last_name?: string;
  telegram_photo_url?: string;
  display_name?: string | null;
  profile_picture_url?: string | null;
  role: string;
  created_at: string;
  learner_onboarding_completed_at?: string | null;
  bio?: string;
  csrf_token?: string;
}

/**
 * Get Current Session
 * Retrieves the current user session via httpOnly cookies
 *
 * Uses manual fetch (instead of apiClient) so we can handle token refresh
 * during SSR. The apiClient's refresh flow relies on `credentials: 'include'`
 * which doesn't forward cookies in server-side fetch — so we manually
 * forward cookies from the incoming browser request and forward Set-Cookie
 * headers back to the browser.
 */
async function getSessionState(): Promise<SessionLoadResult<UserSession>> {
  logger.info('getSessionServer', 'Fetching current session');

  try {
    const { getRequest, appendResponseHeader } = await import('@tanstack/react-start/server');
    const request = getRequest();
    const cookieHeader = request.headers.get('cookie');

    const buildHeaders = (cookies: string | null): Record<string, string> => {
      // Rate-limit identity: forward the original client IP as a single-hop
      // X-Forwarded-For (or none for portal-internal calls). Same rule as
      // ssr-fetch.forwardedHeaders — without it every user's session fetch
      // shares the portal container's address as one rate-limit identity.
      const h: Record<string, string> = {
        'Content-Type': 'application/json',
        ...clientIpForwardHeaders(request.headers),
      };
      if (cookies) {
        h['cookie'] = cookies;
        const csrfToken = extractCookieHeaderValue(cookies, getCookieName('csrf_token'));
        if (csrfToken) h['X-CSRF-Token'] = csrfToken;
      }
      return h;
    };

    const forwardSetCookies = (setCookies: string[]) => {
      if (setCookies.length === 0) return;
      // react-start 1.169.x removed getResponse(); appendResponseHeader merges
      // set-cookie values with the same dedupe semantics as Headers.append.
      appendResponseHeader('Set-Cookie', setCookies);
    };

    const result = await loadSessionWithRefresh({
      cookieHeader,
      accessTokenName: getCookieName('access_token'),
      refreshTokenName: getCookieName('refresh_token'),
      sessionUrl: `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.SESSION}`,
      refreshUrl: `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      fetchImpl: fetch,
      buildHeaders,
      forwardSetCookies,
      // Rotated cookies must be shared with child SSR loaders in this same
      // navigation: getRequest() still returns the original (expired) header
      // for every loader, so ssr-fetch consults this overlay.
      onAccessTokenRotated: (rotatedCookies) => setRequestCookieOverlay(request, rotatedCookies),
      toSession: (payload) => {
        const sessionPayload = payload as { user?: UserSession };
        return sessionPayload.user ?? (payload as UserSession);
      },
    });

    // NOTE: Do NOT forward Set-Cookie from the session response here.
    // The go-backend's GetSession re-sets the access_token cookie (sliding
    // expiration) on every 200 — forwarding it makes every authed SSR page carry
    // Set-Cookie → Railway CDN (hikari) bypasses caching. The client-side
    // useSession hook refreshes the cookie independently (separate fetch).

    if (result.status === 'authenticated') {
      logger.info('getSessionServer', 'Session retrieved successfully');
    } else if (result.status === 'unauthenticated') {
      logger.info('getSessionServer', 'Unauthorized - no valid session');
    } else {
      logger.error('getSessionServer', 'Session fetch failed', { reason: result.reason });
    }
    return result;
  } catch (error) {
    logger.error('getSessionServer', error, { message: 'Session fetch failed' });
    return { status: 'unavailable', reason: 'session_failed' };
  }
}

/**
 * Typed SSR result for the root loader. Callers must preserve `unavailable`
 * rather than treating a backend outage as a logout.
 */
export const getSessionStateServer = createServerFn({ method: 'GET' }).handler(async () => {
  'use server';
  return getSessionState();
});

/**
 * Logout User
 * Destroys the current session. The backend revokes via valid access claims
 * OR the refresh token, so an expired access token does not block logout
 * (#601). If the backend cannot complete, auth cookies are still cleared
 * through the SSR response so a reload cannot recover the session.
 */
export const logoutServer = createServerFn({ method: 'POST' }).handler(async () => {
  'use server';
  logger.info('logoutServer', 'Logging out user');

  const { getRequest, appendResponseHeader } = await import('@tanstack/react-start/server');
  const request = getRequest();
  const cookieHeader = request.headers.get('cookie');

  logger.info('logoutServer', 'Incoming cookies', {
    hasCookie: !!cookieHeader,
    cookieLength: cookieHeader?.length,
  });

  const buildHeaders = (cookies: string | null): Record<string, string> => {
    // Same rate-limit identity rule as getSessionState / ssr-fetch.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...clientIpForwardHeaders(request.headers),
    };
    if (cookies) {
      headers['cookie'] = cookies;
      // Extract CSRF token for double-submit cookie pattern
      const csrfToken = extractCookieHeaderValue(cookies, getCookieName('csrf_token'));
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return headers;
  };

  const forwardSetCookies = (setCookies: string[]) => {
    if (setCookies.length === 0) return;
    // react-start 1.169.x removed getResponse(); appendResponseHeader throws
    // outside a request scope, which the surrounding try/catch reports.
    appendResponseHeader('Set-Cookie', setCookies);
  };

  try {
    return await logoutWithBackend({
      cookieHeader,
      // Names must carry the environment prefix (e.g. stg_) or exact matching
      // in buildClearCookieHeaders drops them and staging keeps its session.
      authCookieNames: ['access_token', 'refresh_token', 'csrf_token'].map(getCookieName),
      logoutUrl: `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.LOGOUT}`,
      fetchImpl: fetch,
      buildHeaders,
      forwardSetCookies,
    });
  } catch (error) {
    logger.error('logoutServer', error);
    throw error;
  }
});
